import { Icon } from '@/models/interfaces'
import { DataTableHeader } from 'vuetify'
import { Process } from '@/entities/settings'

export enum CellType {
  text = 'text-cell',
  status = 'status-cell',
  action = 'action-cell',
  linked = 'linked-cell',
  expand = 'expand-cell',
  price = 'price-cell',
  alert = 'alert-cell',
  icon = 'icon-cell',
  stock = 'stock-cell',
  progress = 'progress-cell',
  rating = 'rating-cell',
  address = 'address-cell',
  cellList = 'list-component-cell',
  date = 'date-cell',
  inspectionSummary = 'inspection-summary-cell',
  photo = 'photo-cell',
  letterApproval = 'letter-approval-cell',
  backupPayment = 'backup-payment-cell',
  chips = 'chips-cell',
  pdfReserve = 'pdf-reserve-cell',
  pdfSale = 'pdf-sale-cell',
  pdfResponsability = 'pdf-responsability-cell',
  pdfPurchase = 'pdf-purchase-cell',
  pdfWarranty = 'pdf-warranty-cell',
  alertReserveSell = 'alert-reserve-sell-cell'
}
// ---------------------------------------------------------------------------------------------------------------------
export interface PdfPurchaseCellHeader extends CellHeader {
  type: CellType.pdfPurchase
  options?: LinkedCellOptions
}

export type PdfPurchaseCellDefinition = Omit<PdfPurchaseCellHeader, 'text' | 'value'>
// ---------------------------------------------------------------------------------------------------------------------
export interface PdfResponsabilityCellHeader extends CellHeader {
  type: CellType.pdfResponsability
  options?: LinkedCellOptions
}

export type PdfResponsabilityCellDefinition = Omit<PdfResponsabilityCellHeader, 'text' | 'value'>
// ---------------------------------------------------------------------------------------------------------------------
export interface PdfWarrantyCellHeader extends CellHeader {
  type: CellType.pdfWarranty
  options?: LinkedCellOptions
}

export type PdfWarrantyCellDefinition = Omit<PdfWarrantyCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface PdfSaleCellHeader extends CellHeader {
  type: CellType.pdfSale
  options?: LinkedCellOptions
}

export type PdfSaleCellDefinition = Omit<PdfSaleCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface AlertReserveSellCellHeader extends CellHeader {
  type: CellType.alertReserveSell
  options?: BasicCellOptions
}

export type AlertReserveSellCellDefinition = Omit<AlertReserveSellCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface PdfReserveCellHeader extends CellHeader {
  type: CellType.pdfReserve
  options?: LinkedCellOptions
}

export type PdfReserveCellDefinition = Omit<PdfReserveCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface ChipCellHeader extends CellHeader {
  type: CellType.chips
}

export type ChipCellDefinition = Omit<ChipCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface InspectionSummaryCellHeader extends CellHeader {
  type: CellType.inspectionSummary
}

export type InspectionSummaryCellDefinition = Omit<InspectionSummaryCellHeader, 'text' | 'value'>

// ----------------------------------------------------------------------------------------------------------------------
export interface BackupPaymentCellHeader extends CellHeader {
  type: CellType.backupPayment
  options?: {
    name: string
  }
}

export type BackupPaymentCellDefinition = Omit<BackupPaymentCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface LetterApprovalCellHeader extends CellHeader {
  type: CellType.letterApproval
}

export type LetterApprovalCellDefinition = Omit<LetterApprovalCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface PhotoCellHeader extends CellHeader {
  type: CellType.photo
}

export type PhotoCellDefinition = Omit<PhotoCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface RatingCellHeader extends CellHeader {
  type: CellType.rating
}

export type RatingCellDefinition = Omit<RatingCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface AddressCellHeader extends CellHeader {
  type: CellType.address
}

export type AddressCellDefinition = Omit<AddressCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface ProgressCellHeader extends CellHeader {
  type: CellType.progress
}

export type ProgressCellDefinition = Omit<ProgressCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------

export interface StockCellHeader extends CellHeader {
  type: CellType.stock
  options: StatusCellOptions
}

export type StockCellDefinition = Omit<StockCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------

export interface IconCellHeader extends CellHeader {
  type: CellType.icon
}

export type IconCellDefinition = Omit<IconCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface AlertCellHeader extends CellHeader {
  type: CellType.alert
  options?: BasicCellOptions
}

export type AlertCellDefinition = Omit<AlertCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface StatusCellOptions {
  set: Record<string, Icon>
  allowCreate?: boolean
  showDetails?: boolean
}

export interface StatusCellHeader extends CellHeader {
  type: CellType.status
  options?: StatusCellOptions
}

export type StatusCellDefinition = Omit<StatusCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------

export interface BasicCellOptions {
  tooltip?: boolean
  bold?: boolean
}

export interface PriceCellHeader extends CellHeader {
  type: CellType.price
  options?: BasicCellOptions
}

export type PriceCellDefinition = Omit<PriceCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface PaddingOption {
  py?: number
  px?: number
  pt?: number
  pb?: number
  pl?: number
  pr?: number
}

export interface ListComponentCellOptions {
  component: CellType.status | CellType.price | CellType.alert | CellType.rating | CellType.linked | CellType.action | CellType.text
  cols?: number
  row?: PaddingOption
  col?: PaddingOption
  options?: StatusCellOptions | BasicCellOptions | LinkedCellOptions | PriceCellOptions | TextCellOptions
  trigger?: (item: any) => void
}

export interface ListComponentCellHeader extends CellHeader {
  type: CellType.cellList
  options?: ListComponentCellOptions
}

export type ListComponentCellDefinition = Omit<ListComponentCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface PriceCellOptions {
  colTitle?: number
  colValue?: number
  valueCenter?: boolean
}

export interface LinkedCellOptions {
  path?: string
  tooltip?: string
  disabled?: boolean
  trigger?: (item: any) => void
  allowCreate?: boolean
  disabledActionMissingProperty?: {
    key: string
  }
  action?: {
    trigger?: (item: any, process: Process, system: string) => void
    color?: string
    icon?: string
    only?: boolean
    always?: boolean
    iconColor?: string
    messaging?: boolean
  }
  disabledMissingProperty?: ['phone']
}

export interface LinkedCellHeader extends CellHeader {
  type: CellType.linked
  options?: LinkedCellOptions
}

export type LinkedCellDefinition = Omit<LinkedCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface ExpandCellOptions {
  expandOff?: boolean
  removeOff?: boolean
  editOff?: boolean
  show?: boolean
  action?: {
    [key: string]: (item: any) => void
  }
}

export interface ExpandCellHeader extends CellHeader {
  type: CellType.expand
  options?: ExpandCellOptions
}

export type ExpandCellDefinition = Omit<ExpandCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------
export interface TextCellOptions {
  align: string
}

export interface TextCellHeader extends CellHeader {
  type: CellType.text | CellType.date
  options?: TextCellOptions
}

export type TextCellDefinition = Omit<TextCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------

export interface ActionCellHeader extends CellHeader {
  type: CellType.action
  options?: LinkedCellOptions
}

export type ActionCellDefinition = Omit<ActionCellHeader, 'text' | 'value'>

// ---------------------------------------------------------------------------------------------------------------------

interface CellHeader extends DataTableHeader {
  type: CellType
  options?: Record<string, any>
}

export type TableCellHeader =
  LinkedCellHeader
  | StatusCellHeader
  | TextCellHeader
  | ExpandCellHeader
  | ActionCellHeader
  | PriceCellHeader
  | ListComponentCellHeader
  | AlertCellHeader
  | IconCellHeader
  | StockCellHeader
  | ProgressCellHeader
  | AddressCellHeader
  | RatingCellHeader
  | InspectionSummaryCellHeader
  | PhotoCellHeader
  | LetterApprovalCellHeader
  | BackupPaymentCellHeader
  | ChipCellHeader
  | PdfReserveCellHeader
  | AlertReserveSellCellHeader
  | PdfSaleCellHeader
  | PdfResponsabilityCellHeader
  | PdfPurchaseCellHeader
  | PdfWarrantyCellHeader
